import React, { useEffect, useMemo, useState } from 'react'
import Modal from 'components/atoms/Modal'
import styled, { css } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { selectFileTagsForBp } from 'ducks/files/selectors'
import { ReactComponent as FileIcon } from 'assets/images/file-icon.svg'
import Button from 'components/atoms/Button'
import PrimaryButton from 'components/atoms/PrimaryButton'
import { syncFileTagsInFileRoutine, syncFileTagsInFileVersionRoutine } from 'ducks/files/actions'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import Input from 'components/atoms/Input'
import SearchIcon from '@mui/icons-material/Search'

const getFileExtension = (file, isVersion) => {
  if (isVersion) {
    return file?.path?.split('.')?.pop() || ''
  } else {
    const versions = file?.versions || []
    if (versions[0]) {
      return versions[0].path?.split('.')?.pop() || ''
    } else {
      return ''
    }
  }
}

const AttachTagToFile = ({ file, children, isVersion }) => {
  const bp = useSelector(getSelectedBp)
  const [open, setOpen] = useState(false)
  const tags = useSelector(selectFileTagsForBp)
  const fileExtension = getFileExtension(file, isVersion)
  const fileName = isVersion ? file.name : file.displayName
  const [selectedTags, setSelectedTags] = useState([])
  const [newTags, setNewTags] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [sortAlphabetically, setSortAlphabetically] = useState(true)
  const dispatch = useDispatch()

  const taggings = file?.taggings || []

  useEffect(() => {
    if (open) {
      const currentTags = taggings?.map(tag => tag.tag.id) || []
      setSelectedTags(currentTags)
    }
  }, [open])

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setSelectedTags([])
    setNewTags('')
    setSearchValue('')
  }

  const visibleTags = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase()
    const filtered = (tags || []).filter(tag =>
      !normalizedQuery || tag.name.toLowerCase().includes(normalizedQuery)
    )

    return sortAlphabetically
      ? [...filtered].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      : filtered
  }, [tags, searchValue, sortAlphabetically])

  const toggleTagSelection = id => () => {
    setSelectedTags(prev => {
      if (prev.includes(id)) {
        return prev.filter(el => el !== id)
      } else {
        return [...prev, id]
      }
    })
  }

  const handleSubmit = () => {
    const parsedNewTags = newTags
      .replace(/,\s*/g, ',')
      .split(',')
      .filter(el => isNotNilOrEmpty(el))

    isVersion
      ? dispatch(syncFileTagsInFileVersionRoutine({
        bpId: bp.id,
        fileVersionId: file.id,
        tagIds: selectedTags,
        newTags: parsedNewTags,
        callback: handleClose
      }))
      : dispatch(syncFileTagsInFileRoutine({
        bpId: bp.id,
        fileId: file.id,
        tagIds: selectedTags,
        newTags: parsedNewTags,
        callback: handleClose
      }))
  }

  return (
    <>
      <TriggerWrapper onClick={handleOpen}>
        {children}
      </TriggerWrapper>
      <Modal
        open={open}
        onClose={handleClose}
        title='Attach tag to a file'
      >
        <ModalContent>
          <FileWrapper>
            <IconWrapper>
              <FileIcon />
            </IconWrapper>
            <FileName>
              {`${fileName}.${fileExtension}${isVersion ? ` (v${file.version})` : ''}`}
            </FileName>
          </FileWrapper>
          <ModalDesc>
            Select tags to be assigned to this file:
          </ModalDesc>
          {isNotNilOrEmpty(tags) && (
            <TagsToolbar>
              <SearchField>
                <SearchFieldIcon />
                <SearchInput
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  placeholder='Type to filter tags...'
                />
              </SearchField>
              <SortToggle
                type='button'
                $active={sortAlphabetically}
                onClick={() => setSortAlphabetically(prev => !prev)}
                title='Toggle tag ordering'
              >
                {sortAlphabetically ? 'A–Z' : 'Newest'}
              </SortToggle>
            </TagsToolbar>
          )}
          <TagsList>
            {
              isNotNilOrEmpty(tags) ? (
                isNotNilOrEmpty(visibleTags) ? visibleTags.map(tag => {
                  return (
                    <Tag
                      key={tag.id}
                      active={selectedTags.includes(tag.id)}
                      onClick={toggleTagSelection(tag.id)}
                    >
                      {tag.name}
                    </Tag>
                  )
                }) : (
                  <div>No tags match &quot;{searchValue.trim()}&quot;</div>
                )
              ) : (
                <div>No tags to display</div>
              )
            }
          </TagsList>
          <ModalDesc>
            or add new tags:
          </ModalDesc>
          <Input
            name='newTags'
            label='New tags (comma separated)'
            value={newTags}
            onChange={(_, value) => setNewTags(value)}
          />
          <ButtonsWrapper>
            <Button
              variant='outlined'
              onClick={handleClose}
            >
              Cancel
            </Button>
            <PrimaryButton
              onClick={handleSubmit}
            >
              Save
            </PrimaryButton>
          </ButtonsWrapper>
        </ModalContent>
      </Modal>
    </>
  )
}

export default AttachTagToFile

const TriggerWrapper = styled.div`
  width: fit-content;
  height: fit-content;
`

const ModalContent = styled.div`
  max-width: 500px;
`

const FileName = styled.div`
  font-size: 16px;
  word-break: break-all;
`

const ModalDesc = styled.div`
  margin-bottom: 20px;
`

const TagsToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`

const SearchField = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  background: #fcfcfd;
`

const SearchFieldIcon = styled(SearchIcon)`
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 18px !important;
`

const SearchInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.darkGrey};
`

const SortToggle = styled.button`
  height: 40px;
  min-width: 76px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid ${({ theme, $active }) => $active ? 'rgba(51, 190, 204, 0.35)' : theme.colors.grey[600]};
  background: ${({ $active }) => $active ? 'rgba(51, 190, 204, 0.12)' : '#fff'};
  color: ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.darkGrey};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin-bottom: 30px;
  max-height: 320px;
  overflow-y: auto;
`

const Tag = styled.div`
  padding: 5px 10px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.darkGrey};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  transition: all .3s;
  text-transform: capitalize;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[500]};
  }

  ${({ active }) => active && css`
    background-color: #e6f2ff;
    border-color: #e6f2ff;

    &:hover {
      background-color: #e6f2ff;
      border-color: #e6f2ff;
    }
  `};

  ${({ disabled, theme }) => disabled && css`
    background-color: ${theme.colors.grey[400]};
    border-color: ${theme.colors.grey[400]};
    color: ${theme.colors.grey[700]};
    cursor: default;

    &:hover {
      background-color: ${theme.colors.grey[400]};
      border-color: ${theme.colors.grey[400]};
      color: ${theme.colors.grey[700]};
    }
  `};

`

const FileWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 20px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const ButtonsWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 15px;
  margin-top: 20px;

  button {
    flex: 1;
  }
`
